import { UndeclaredKeyError } from './errors.js';

/**
 * Create data resolver from given promise
 *
 * Resolver allows to access data from promise synchronously. This is needed to
 * make use of React’s Suspense mechanic.
 *
 * @private
 * @param {Promise.<*>} promise
 * @returns {Object}
 *
 *
 */

function createDataResolver(promise) {
  var state = {
    status: null,
    value: null
  };
  var suspender = promise.then(value => {
    state.value = value;
    state.status = "success";
  }, error => {
    state.value = error;
    state.status = "error";
  });
  return {
    /**
     * Attempt reading wrapped promise synchronously
     *
     * @private
     * @typedef {Function} PromiseReader
     *
     * @returns {*} Value that the promise was resolved with
     *
     * @throws {Error} Throws underlying error if the promise was rejected
     * @throws {Promise.<*>} Throws the promise if it was not settled
     */
    read() {
      switch (state.status) {
        case "error":
          {
            throw state.value;
          }

        case "success":
          {
            return state.value;
          }

        default:
          {
            throw suspender;
          }
      }
    },

    /**
     * Wrapped promise
     *
     * @private
     */
    value: promise
  };
}
/**
 * Translation cache map
 *
 * Extends native Map to provide additional heuristics for Data Loader cache
 *
 *
 */


class TranslationCache extends Map {
  /**
   * Get wrapped promise
   *
   * @private
   * @param {string} key
   * @returns {?Promise.<*>}
   */
  get(key) {
    if (super.has(key)) {
      var {
        value
      } = super.get(key);
      return value;
    }
  }
  /**
   * Read wrapped promise synchronously
   *
   * @private
   * @param {string} key
   *
   * @returns {*} Wrapped promise value
   *
   * @throws {Errors.UndeclaredKey} Throws on attempt to read undeclared key
   */


  read(key) {
    if (!super.has(key)) {
      throw new UndeclaredKeyError(key);
    }

    var {
      read
    } = super.get(key);
    return read();
  }
  /**
   * Store given promise as data resolver
   *
   * @private
   * @param {string} key
   * @param {Promise.<*>} value
   */


  set(key, value) {
    super.set(key, createDataResolver(value));
  }

}

export { TranslationCache };
